import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, DatePicker, Button, Space, Input, Select, message } from 'antd';
import { SearchOutlined, ClearOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import moment from 'moment';
import * as XLSX from 'xlsx';
import DashboardBreadcrumb from '../../components/layout/DashboardBreadcrumb';
import PageLoader from '../../components/sub/PageLoader';
import GetAllTimesheetsApi from '../../api/timesheets/GetAllTimesheetsApi';

const { RangePicker } = DatePicker;
const { Option } = Select;

function Timesheets() {
  const [loading, setLoading] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [filteredTimesheets, setFilteredTimesheets] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [uniquePlans, setUniquePlans] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);

  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  const loadTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetAllTimesheetsApi(perfCycleState.id);
      if (response.success) {
        setTimesheets(response.data);
        setFilteredTimesheets(response.data);
        
        // Extract unique parent plans and users for filtering
        const plans = [...new Set(response.data.map(t => t.parentPlanTitle).filter(Boolean))];
        const users = [...new Set(response.data.map(t => t.employeeName).filter(Boolean))];
        setUniquePlans(plans);
        setUniqueUsers(users);
      } else {
        message.error('Failed to load timesheets');
      }
    } catch (error) {
      console.error('Error loading timesheets:', error);
      message.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [perfCycleState.id]);

  useEffect(() => {
    if (perfCycleState.id) {
      loadTimesheets();
    }
  }, [perfCycleState.id, loadTimesheets]);

  // Apply filters
  useEffect(() => {
    let filtered = [...timesheets];

    // Date range filter
    if (dateRange.length === 2) {
      filtered = filtered.filter(timesheet => {
        const workDate = moment(timesheet.workDate);
        return workDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
      });
    }

    // Search filter
    if (searchText) {
      filtered = filtered.filter(timesheet =>
        timesheet.splitPlanTitle?.toLowerCase().includes(searchText.toLowerCase()) ||
        timesheet.parentPlanTitle?.toLowerCase().includes(searchText.toLowerCase()) ||
        timesheet.description?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Plan filter
    if (planFilter) {
      filtered = filtered.filter(timesheet => timesheet.parentPlanTitle === planFilter);
    }

    // User filter
    if (userFilter) {
      filtered = filtered.filter(timesheet => timesheet.employeeName === userFilter);
    }

    setFilteredTimesheets(filtered);
  }, [timesheets, dateRange, searchText, planFilter, userFilter]);

  const columns = [
    {
      title: 'Work Date',
      dataIndex: 'workDate',
      key: 'workDate',
      width: 120,
      render: (date) => moment(date).format('MMM DD, YYYY'),
      sorter: (a, b) => moment(a.workDate).unix() - moment(b.workDate).unix(),
    },
    {
      title: 'Employee',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 150,
      ellipsis: true,
      sorter: (a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''),
    },
    {
      title: 'Split Plan',
      dataIndex: 'splitPlanTitle',
      key: 'splitPlanTitle',
      width: 250,
      ellipsis: {
        showTitle: true,
      },
      render: (text) => (
        <div 
          style={{ 
            backgroundColor: '#f5f5f5',
            color: '#262626',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            border: '1px solid #d9d9d9'
          }}
          title={text}
        >
          {text}
        </div>
      ),
    },
    {
      title: 'Parent Plan',
      dataIndex: 'parentPlanTitle',
      key: 'parentPlanTitle',
      width: 250,
      ellipsis: {
        showTitle: true,
      },
      render: (text) => (
        <div 
          style={{ 
            backgroundColor: text ? '#000000' : '#f5f5f5',
            color: text ? '#ffffff' : '#999999',
            border: `1px solid ${text ? '#333333' : '#d9d9d9'}`,
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%'
          }}
          title={text || 'N/A'}
        >
          {text || 'N/A'}
        </div>
      ),
    },
    {
      title: 'Hours Worked',
      dataIndex: 'hoursWorked',
      key: 'hoursWorked',
      width: 120,
      render: (hours) => `${hours} hrs`,
      sorter: (a, b) => a.hoursWorked - b.hoursWorked,
    },
    {
      title: 'Hourly Rate',
      key: 'hourlyRate',
      width: 100,
      render: (_, record) => {
        // Extract hourly rate from user data if available
        const rate = record.hourlyRate || record.user?.hourlyRate || 0;
        return rate > 0 ? `$${parseFloat(rate).toFixed(2)}` : <span className="text-gray-400">No rate</span>;
      },
      sorter: (a, b) => (parseFloat(a.hourlyRate || a.user?.hourlyRate) || 0) - (parseFloat(b.hourlyRate || b.user?.hourlyRate) || 0),
    },
    {
      title: 'Cost',
      key: 'payment',
      width: 120,
      render: (_, record) => {
        const hours = parseFloat(record.hoursWorked) || 0;
        const rate = parseFloat(record.hourlyRate || record.user?.hourlyRate) || 0;
        const payment = hours * rate;
        
        if (rate > 0) {
          return (
            <div className="text-green-600 font-medium">
              ${payment.toFixed(2)}
            </div>
          );
        }
        return <span className="text-gray-400">-</span>;
      },
      sorter: (a, b) => {
        const paymentA = (parseFloat(a.hoursWorked) || 0) * (parseFloat(a.hourlyRate) || 0);
        const paymentB = (parseFloat(b.hoursWorked) || 0) * (parseFloat(b.hourlyRate) || 0);
        return paymentA - paymentB;
      },
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 100,
      render: (time) => moment(time).format('HH:mm'),
      sorter: (a, b) => moment(a.startTime).unix() - moment(b.startTime).unix(),
    },
    {
      title: 'End Time',
      dataIndex: 'endTime',
      key: 'endTime',
      width: 100,
      render: (time) => moment(time).format('HH:mm'),
      sorter: (a, b) => moment(a.endTime).unix() - moment(b.endTime).unix(),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
  ];

  const handleClearFilters = () => {
    setDateRange([]);
    setSearchText('');
    setPlanFilter('');
    setUserFilter('');
  };

  const handleExportBillingToExcel = () => {
    try {
      // Prepare billing data for export - use filtered data
      const billingData = filteredTimesheets.map((record, index) => {
        const hours = parseFloat(record.hoursWorked) || 0;
        const rate = parseFloat(record.hourlyRate || record.user?.hourlyRate) || 0;
        const payment = hours * rate;
        
        return {
          'S.No': index + 1,
          'Employee': record.employeeName || 'N/A',
          'Work Date': moment(record.workDate).format('MMM DD, YYYY'),
          'Start Time': moment(record.startTime).format('HH:mm'),
          'End Time': moment(record.endTime).format('HH:mm'),
          'Hours Worked': hours.toFixed(2),
          'Hourly Rate': rate > 0 ? rate.toFixed(2) : '0.00',
          'Cost': payment.toFixed(2)
        };
      });

      // Calculate totals
      const totalHours = filteredTimesheets.reduce((sum, t) => sum + (parseFloat(t.hoursWorked) || 0), 0);
      const totalPayment = filteredTimesheets.reduce((sum, t) => {
        const hours = parseFloat(t.hoursWorked) || 0;
        const rate = parseFloat(t.hourlyRate || t.user?.hourlyRate) || 0;
        return sum + (hours * rate);
      }, 0);

      // Add totals row
      billingData.push({
        'S.No': '',
        'Employee': 'TOTAL',
        'Work Date': '',
        'Start Time': '',
        'End Time': '',
        'Hours Worked': totalHours.toFixed(2),
        'Hourly Rate': '',
        'Cost': totalPayment.toFixed(2)
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(billingData);

      // Set column widths for better formatting
      const colWidths = [
        { wch: 8 },   // S.No
        { wch: 20 },  // Employee
        { wch: 15 },  // Work Date
        { wch: 12 },  // Start Time
        { wch: 12 },  // End Time
        { wch: 15 },  // Hours Worked
        { wch: 12 },  // Hourly Rate
        { wch: 12 }   // Cost
      ];
      ws['!cols'] = colWidths;

      // Apply formatting
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Style header row (row 1)
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;
        
        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "366092" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }

      // Style data rows with borders and special formatting
      for (let row = 1; row < billingData.length - 1; row++) {
        const isEvenRow = row % 2 === 0;
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;
          
          // Special formatting for currency and number columns
          let cellStyle = {
            fill: { fgColor: { rgb: isEvenRow ? "F8F9FA" : "FFFFFF" } },
            border: {
              top: { style: "thin", color: { rgb: "CCCCCC" } },
              bottom: { style: "thin", color: { rgb: "CCCCCC" } },
              left: { style: "thin", color: { rgb: "CCCCCC" } },
              right: { style: "thin", color: { rgb: "CCCCCC" } }
            },
            alignment: { horizontal: "center", vertical: "center" }
          };

          // Apply currency formatting to Cost column (col 7)
          if (col === 7) {
            cellStyle.numFmt = '"$"#,##0.00';
            cellStyle.alignment = { horizontal: "right", vertical: "center" };
          }
          // Apply number formatting to Hours and Rate columns (col 5, 6)
          else if (col === 5 || col === 6) {
            cellStyle.numFmt = '#,##0.00';
            cellStyle.alignment = { horizontal: "right", vertical: "center" };
          }
          
          ws[cellAddress].s = cellStyle;
        }
      }

      // Style totals row (last row)
      const totalRowIndex = billingData.length - 1;
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
        if (!ws[cellAddress]) continue;
        
        let totalStyle = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4CAF50" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thick", color: { rgb: "000000" } },
            bottom: { style: "thick", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };

        // Apply currency formatting to Cost column (col 7) and number formatting to Hours (col 5)
        if (col === 7) {
          totalStyle.numFmt = '"$"#,##0.00';
          totalStyle.alignment = { horizontal: "right", vertical: "center" };
        } else if (col === 5) {
          totalStyle.numFmt = '#,##0.00';
          totalStyle.alignment = { horizontal: "right", vertical: "center" };
        }
        
        ws[cellAddress].s = totalStyle;
      }
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Billing Report');

      // Generate filename
      const currentDate = moment().format('YYYY-MM-DD');
      let filename = `Billing_Report_${currentDate}`;
      
      if (dateRange.length === 2) {
        const startDate = moment(dateRange[0]).format('MMM-DD');
        const endDate = moment(dateRange[1]).format('MMM-DD');
        filename += `_${startDate}_to_${endDate}`;
      }
      
      if (userFilter) {
        filename += `_${userFilter.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      
      if (planFilter) {
        filename += `_${planFilter.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      
      filename += '.xlsx';

      // Write and download file
      XLSX.writeFile(wb, filename);
      
      message.success(`Billing report downloaded: ${filename}`);
    } catch (error) {
      console.error('Error exporting billing report:', error);
      message.error('Failed to export billing report. Please try again.');
    }
  };

  const handleExportToExcel = () => {
    try {
      // Prepare data for export - use filtered data
      const exportData = filteredTimesheets.map((record, index) => {
        const hours = parseFloat(record.hoursWorked) || 0;
        const rate = parseFloat(record.hourlyRate || record.user?.hourlyRate) || 0;
        const payment = hours * rate;
        
        return {
          'S.No': index + 1,
          'Work Date': moment(record.workDate).format('MMM DD, YYYY'),
          'Employee': record.employeeName || 'N/A',
          'Split Plan': record.splitPlanTitle || 'N/A',
          'Parent Plan': record.parentPlanTitle || 'N/A',
          'Start Time': moment(record.startTime).format('HH:mm'),
          'End Time': moment(record.endTime).format('HH:mm'),
          'Hours Worked': `${record.hoursWorked} hrs`,
          'Hourly Rate': rate > 0 ? `$${rate.toFixed(2)}` : 'No rate',
          'Cost': rate > 0 ? `$${payment.toFixed(2)}` : '-',
          'Description': record.description || 'N/A'
        };
      });

      // Calculate totals
      const totalHours = filteredTimesheets.reduce((sum, t) => sum + (parseFloat(t.hoursWorked) || 0), 0);
      const totalPayment = filteredTimesheets.reduce((sum, t) => {
        const hours = parseFloat(t.hoursWorked) || 0;
        const rate = parseFloat(t.hourlyRate || t.user?.hourlyRate) || 0;
        return sum + (hours * rate);
      }, 0);

      // Add totals row
      exportData.push({
        'S.No': '',
        'Work Date': '',
        'Employee': 'TOTAL',
        'Split Plan': '',
        'Parent Plan': '',
        'Hours Worked': `${totalHours.toFixed(2)} hrs`,
        'Hourly Rate': '',
        'Cost': totalPayment > 0 ? `$${totalPayment.toFixed(2)}` : '-',
        'Start Time': '',
        'End Time': '',
        'Description': ''
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for better formatting
      const colWidths = [
        { wch: 8 },   // S.No
        { wch: 15 },  // Work Date
        { wch: 20 },  // Employee
        { wch: 30 },  // Split Plan
        { wch: 30 },  // Parent Plan
        { wch: 12 },  // Start Time
        { wch: 12 },  // End Time
        { wch: 15 },  // Hours Worked
        { wch: 12 },  // Hourly Rate
        { wch: 12 },  // Cost
        { wch: 40 }   // Description
      ];
      ws['!cols'] = colWidths;

      // Apply formatting
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Style header row (row 1)
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;
        
        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "1890FF" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }

      // Style data rows with borders and alternating colors
      for (let row = 1; row < exportData.length - 1; row++) {
        const isEvenRow = row % 2 === 0;
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;
          
          // Special formatting for currency and number columns
          let cellStyle = {
            fill: { fgColor: { rgb: isEvenRow ? "F8F9FA" : "FFFFFF" } },
            border: {
              top: { style: "thin", color: { rgb: "E0E0E0" } },
              bottom: { style: "thin", color: { rgb: "E0E0E0" } },
              left: { style: "thin", color: { rgb: "E0E0E0" } },
              right: { style: "thin", color: { rgb: "E0E0E0" } }
            },
            alignment: { horizontal: "center", vertical: "center" }
          };

          // Apply currency formatting to Hourly Rate (col 6) and Cost (col 7) columns
          if (col === 6 || col === 7) {
            cellStyle.numFmt = '"$"#,##0.00';
            cellStyle.alignment = { horizontal: "right", vertical: "center" };
          }
          // Apply number formatting to Hours Worked column (col 5)
          else if (col === 5) {
            cellStyle.numFmt = '#,##0.00';
            cellStyle.alignment = { horizontal: "right", vertical: "center" };
          }
          
          ws[cellAddress].s = cellStyle;
        }
      }

      // Style totals row (last row)
      const totalRowIndex = exportData.length - 1;
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
        if (!ws[cellAddress]) continue;
        
        let totalStyle = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "FF6B35" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thick", color: { rgb: "000000" } },
            bottom: { style: "thick", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };

        // Apply currency formatting to Hourly Rate (col 6) and Cost (col 7) columns
        if (col === 6 || col === 7) {
          totalStyle.numFmt = '"$"#,##0.00';
          totalStyle.alignment = { horizontal: "right", vertical: "center" };
        }
        // Apply number formatting to Hours Worked column (col 5)
        else if (col === 5) {
          totalStyle.numFmt = '#,##0.00';
          totalStyle.alignment = { horizontal: "right", vertical: "center" };
        }
        
        ws[cellAddress].s = totalStyle;
      }

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Timesheets');

      // Generate filename with current date and filter info
      const currentDate = moment().format('YYYY-MM-DD');
      let filename = `Timesheets_${currentDate}`;
      
      if (dateRange.length === 2) {
        const startDate = moment(dateRange[0]).format('MMM-DD');
        const endDate = moment(dateRange[1]).format('MMM-DD');
        filename += `_${startDate}_to_${endDate}`;
      }
      
      if (userFilter) {
        filename += `_${userFilter.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      
      if (planFilter) {
        filename += `_${planFilter.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      
      filename += '.xlsx';

      // Write and download file
      XLSX.writeFile(wb, filename);
      
      message.success(`Excel file downloaded: ${filename}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      message.error('Failed to export to Excel. Please try again.');
    }
  };

  if (loading && timesheets.length === 0) {
    return <PageLoader />;
  }

  return (
    <div className="p-6">
      <DashboardBreadcrumb
        items={[
          { title: 'Dashboard', path: '/dashboard' },
          { title: 'Timesheets' }
        ]}
      />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Timesheets</h1>
        <p className="text-gray-600">
          View all timesheet entries from split plans with billing calculations. Filter by date, employee, or parent plan and export detailed reports.
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="MMM DD, YYYY"
              placeholder={['Start Date', 'End Date']}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <Input
              placeholder="Search plans or description..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parent Plan
            </label>
            <Select
              placeholder="Filter by parent plan"
              value={planFilter}
              onChange={setPlanFilter}
              style={{ width: 200 }}
              allowClear
            >
              {uniquePlans.map(plan => (
                <Option key={plan} value={plan}>{plan}</Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee
            </label>
            <Select
              placeholder="Filter by employee"
              value={userFilter}
              onChange={setUserFilter}
              style={{ width: 200 }}
              allowClear
            >
              {uniqueUsers.map(user => (
                <Option key={user} value={user}>{user}</Option>
              ))}
            </Select>
          </div>

          <div className="flex gap-2 mt-6">
            <Button 
              icon={<ClearOutlined />} 
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadTimesheets}
              loading={loading}
            >
              Refresh
            </Button>
            <Button 
              type="primary"
              icon={<DownloadOutlined />} 
              onClick={handleExportToExcel}
              disabled={filteredTimesheets.length === 0}
            >
              Export Full Report
            </Button>
            <Button 
              type="default"
              icon={<DownloadOutlined />} 
              onClick={handleExportBillingToExcel}
              disabled={filteredTimesheets.length === 0}
              className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
            >
              Export Billing Report
            </Button>
          </div>
        </div>
      </Card>

      {/* Results Summary with Billing */}
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-sm text-blue-600 font-medium">Total Entries</div>
            <div className="text-2xl font-bold text-blue-800">
              {filteredTimesheets.length}
            </div>
            <div className="text-xs text-blue-500">
              of {timesheets.length} total
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-sm text-green-600 font-medium">Total Hours</div>
            <div className="text-2xl font-bold text-green-800">
              {filteredTimesheets.reduce((sum, t) => sum + (parseFloat(t.hoursWorked) || 0), 0).toFixed(1)}
            </div>
            <div className="text-xs text-green-500">hours worked</div>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-sm text-purple-600 font-medium">Total Cost</div>
            <div className="text-2xl font-bold text-purple-800">
              ${filteredTimesheets.reduce((sum, t) => {
                const hours = parseFloat(t.hoursWorked) || 0;
                const rate = parseFloat(t.hourlyRate || t.user?.hourlyRate) || 0;
                return sum + (hours * rate);
              }, 0).toFixed(2)}
            </div>
            <div className="text-xs text-purple-500">total billing</div>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="text-sm text-orange-600 font-medium">Avg Rate</div>
            <div className="text-2xl font-bold text-orange-800">
              ${(() => {
                const ratesWithValues = filteredTimesheets.filter(t => parseFloat(t.hourlyRate || t.user?.hourlyRate) > 0);
                const avgRate = ratesWithValues.length > 0 
                  ? ratesWithValues.reduce((sum, t) => sum + parseFloat(t.hourlyRate || t.user?.hourlyRate), 0) / ratesWithValues.length 
                  : 0;
                return avgRate.toFixed(2);
              })()}
            </div>
            <div className="text-xs text-orange-500">per hour</div>
          </div>
        </div>
      </div>

      {/* Timesheets Table */}
      <Card style={{ backgroundColor: '#ffffff', border: '1px solid #f0f0f0', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
        <Table
          columns={columns}
          dataSource={filteredTimesheets}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: 1600 }}
          size="middle"
          className="white-table"
        />
      </Card>

      {/* Custom CSS for clean white table */}
      <style jsx>{`
        .white-table .ant-table {
          background-color: #ffffff !important;
          color: #262626 !important;
          border-radius: 8px !important;
          overflow: hidden !important;
        }
        
        .white-table .ant-table-thead > tr > th {
          background-color: #fafafa !important;
          color: #262626 !important;
          border-bottom: 1px solid #f0f0f0 !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          padding: 16px 12px !important;
        }
        
        .white-table .ant-table-tbody > tr > td {
          background-color: #ffffff !important;
          color: #262626 !important;
          border-bottom: 1px solid #f0f0f0 !important;
          padding: 12px !important;
          font-size: 13px !important;
        }
        
        .white-table .ant-table-tbody > tr:nth-child(even) > td {
          background-color: #fafafa !important;
        }
        
        .white-table .ant-table-tbody > tr:hover > td {
          background-color: #f5f5f5 !important;
          color: #262626 !important;
        }
        
        .white-table .ant-pagination {
          background-color: transparent !important;
          margin-top: 16px !important;
        }
        
        .white-table .ant-pagination .ant-pagination-item {
          background-color: #ffffff !important;
          border-color: #d9d9d9 !important;
          color: #262626 !important;
        }
        
        .white-table .ant-pagination .ant-pagination-item a {
          color: #262626 !important;
        }
        
        .white-table .ant-pagination .ant-pagination-item:hover {
          border-color: #1890ff !important;
        }
        
        .white-table .ant-pagination .ant-pagination-item-active {
          background-color: #1890ff !important;
          border-color: #1890ff !important;
        }
        
        .white-table .ant-pagination .ant-pagination-item-active a {
          color: #ffffff !important;
        }
        
        .white-table .ant-pagination .ant-pagination-prev,
        .white-table .ant-pagination .ant-pagination-next {
          color: #262626 !important;
        }
        
        .white-table .ant-pagination .ant-pagination-options {
          color: #262626 !important;
        }
        
        .white-table .ant-select-selector {
          background-color: #ffffff !important;
          color: #262626 !important;
          border-color: #d9d9d9 !important;
        }
        
        .white-table .ant-input {
          background-color: #ffffff !important;
          color: #262626 !important;
          border-color: #d9d9d9 !important;
        }
        
        .white-table .ant-table-container {
          border-radius: 8px !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
        }
      `}</style>
    </div>
  );
}

export default Timesheets;
